import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * Controls the WhatsApp Checkout modal: a premium alternative checkout path
 * that collects delivery details and hands the customer off to WhatsApp with
 * a formatted order summary, without replacing Shopify's native checkout.
 *
 * Mounted once, globally, by `snippets/whatsapp-checkout-modal.liquid`, and
 * opened from any `cart-summary` instance (drawer or cart page) via
 * `on:click="#whatsapp-checkout-modal/openFromCart"`.
 *
 * @extends DialogComponent
 */
export class WhatsappCheckoutModal extends DialogComponent {
  /** localStorage key for persisted customer field values. */
  static #STORAGE_KEY = 'whatsapp-checkout-customer';

  /**
   * The current cart snapshot, read fresh from the DOM each time the modal
   * opens. `null` until `openFromCart` has run at least once, or if the
   * payload could not be read.
   * @type {WhatsappCartPayload | null}
   */
  #cartPayload = null;

  /**
   * This feature's translated UI strings, rendered once by
   * `whatsapp-checkout-modal.liquid` via Shopify's `| t` filter. Cached
   * after first read since they never change during the page's lifetime.
   * @type {Record<string, string> | null}
   */
  #strings = null;

  /**
   * Guards against submitting the order more than once while the success
   * state is being shown and the dialog is closing.
   * @type {boolean}
   */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('input', this.#handleFieldChange);
    this.addEventListener('change', this.#handleFieldChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('input', this.#handleFieldChange);
    this.removeEventListener('change', this.#handleFieldChange);
  }

  /**
   * Opens the modal with an already-built payload and runs the full
   * opening sequence: reset the view, clear stale validation state,
   * restore persisted customer data, then show the dialog. Shared by
   * every entry point — the cart button (`openFromCart`) and any
   * product-page "Buy via WhatsApp" button (`openFromProduct`) — so there
   * is exactly one modal-opening sequence regardless of where the order
   * data came from.
   *
   * @param {WhatsappCartPayload | null} payload
   */
  #open(payload) {
    this.#showView('form');
    this.#isSubmitting = false;
    this.#cartPayload = payload;
    this.#clearAllFieldErrors();
    this.#restoreCustomerData();
    this.showDialog();
  }

  /**
   * Opens the modal using the current cart's contents.
   *
   * The cart payload is re-read from the DOM on every open rather than
   * cached, because the Section Rendering API can morph
   * `#whatsapp-checkout-payload` at any time after a cart change, and this
   * is the one moment we know for certain the customer wants current data.
   */
  openFromCart = () => {
    this.#open(this.#readCartPayload());
  };

  /**
   * Opens the modal using a single product's currently selected variant
   * and quantity, built by a `WhatsappBuyButtonComponent` on a product
   * page. The payload has the exact same shape the cart already produces,
   * so nothing downstream (validation, Review, the message) needs to know
   * which entry point was used.
   *
   * @param {WhatsappCartPayload} payload
   */
  openFromProduct = (payload) => {
    this.#open(payload);
  };

  /**
   * Reads and parses the cart JSON payload rendered by `cart-summary.liquid`.
   *
   * @returns {WhatsappCartPayload | null} The parsed payload, or `null` if
   *   it is missing or malformed.
   */
  #readCartPayload() {
    const node = document.getElementById('whatsapp-checkout-payload');
    if (!(node instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(node.textContent ?? '');
    } catch (error) {
      console.warn('[whatsapp-checkout] Could not parse cart payload:', error);
      return null;
    }
  }

  /**
   * Reads one of this feature's translated UI strings.
   *
   * @param {string} key
   * @returns {string}
   */
  #i18n(key) {
    if (!this.#strings) {
      const node = document.getElementById('whatsapp-checkout-i18n');
      try {
        this.#strings = node instanceof HTMLScriptElement ? JSON.parse(node.textContent ?? '') : {};
      } catch (error) {
        console.warn('[whatsapp-checkout] Could not parse i18n strings:', error);
        this.#strings = {};
      }
    }
    return this.#strings[key] ?? '';
  }

  /**
   * The customer-field registry, generated fresh from the DOM every time
   * it's read. This is the single source of truth for every customer
   * field — adding, removing, or reordering a field only requires editing
   * `whatsapp-checkout-modal.liquid`; nothing here needs to change.
   *
   * Consumed by validation, localStorage persistence, the Review screen,
   * and the WhatsApp message (added in later phases).
   *
   * @returns {WhatsappFieldDescriptor[]}
   */
  get fields() {
    return (this.refs.field ?? []).map((element) => ({
      key: element.dataset.field ?? '',
      element,
      required: element.required,
      includeInReview: element.dataset.review === 'true',
      includeInMessage: element.dataset.message === 'true',
      role: element.dataset.fieldRole,
    }));
  }

  /**
   * Finds a field by its behavioral role (`data-field-role`) rather than by
   * its `data-field` key, so this keeps working even if a field's key
   * changes — the only field-specific lookup in this file.
   *
   * @param {string} role
   * @returns {WhatsappFieldDescriptor | undefined}
   */
  #findFieldByRole(role) {
    return this.fields.find((field) => field.role === role);
  }

  /**
   * Reads a field's human-readable value: the selected option's visible
   * text for `<select>` elements, or the trimmed value otherwise.
   *
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
   * @returns {string}
   */
  #getFieldDisplayValue(element) {
    if (element instanceof HTMLSelectElement) {
      return element.options[element.selectedIndex]?.text.trim() ?? '';
    }
    return element.value.trim();
  }

  /**
   * Syncs the State/Province field's control type to match the currently
   * selected country. Shopify's `all_country_option_tags` embeds a
   * `data-provinces` JSON array on every country `<option>`; when that
   * array is non-empty we render a `<select>` of Shopify's own province
   * list, otherwise we fall back to free text. This is the entire
   * mechanism that keeps this feature country-agnostic — there is no
   * per-country branching anywhere in this file.
   */
  handleCountryChange = () => {
    const countryField = this.#findFieldByRole('country');
    const regionField = this.#findFieldByRole('region');
    if (!countryField || !regionField) return;

    const select = /** @type {HTMLSelectElement} */ (countryField.element);
    const selectedOption = select.options[select.selectedIndex];
    const provinces = this.#parseProvinces(selectedOption);

    this.#renderRegionField(
      /** @type {HTMLInputElement | HTMLSelectElement} */ (regionField.element),
      provinces
    );
  };

  /**
   * @param {HTMLOptionElement | undefined} option
   * @returns {[string, string][]} Array of [name, code] pairs, or an empty array.
   */
  #parseProvinces(option) {
    if (!option) return [];

    try {
      const parsed = JSON.parse(option.dataset.provinces ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[whatsapp-checkout] Could not parse province data:', error);
      return [];
    }
  }

  /**
   * Replaces the current State/Province field with the correct control
   * type, carrying over its existing value (matched by text where
   * possible) and every discovery/accessibility attribute, so the
   * replacement is picked up by `Component`'s own ref refresh with no
   * extra wiring.
   *
   * @param {HTMLInputElement | HTMLSelectElement} currentElement
   * @param {[string, string][]} provinces
   */
  #renderRegionField(currentElement, provinces) {
    const shouldBeSelect = provinces.length > 0;
    const isSelect = currentElement instanceof HTMLSelectElement;
    if (shouldBeSelect === isSelect) return;

    const currentValue = this.#getFieldDisplayValue(currentElement);
    const replacement = shouldBeSelect
      ? this.#buildRegionSelect(provinces, currentValue)
      : this.#buildRegionInput(currentValue);

    for (const attribute of ['id', 'name', 'required', 'aria-describedby', 'autocomplete', 'aria-invalid']) {
      const value = currentElement.getAttribute(attribute);
      if (value !== null) replacement.setAttribute(attribute, value);
    }
    replacement.setAttribute('ref', 'field[]');
    replacement.dataset.field = currentElement.dataset.field ?? '';
    replacement.dataset.fieldRole = 'region';
    replacement.dataset.review = currentElement.dataset.review ?? 'true';
    replacement.dataset.message = currentElement.dataset.message ?? 'true';
    replacement.className = currentElement.className;

    currentElement.replaceWith(replacement);
  }

  /**
   * @param {[string, string][]} provinces
   * @param {string} currentValue - Previous value to preserve, matched by text.
   * @returns {HTMLSelectElement}
   */
  #buildRegionSelect(provinces, currentValue) {
    const select = document.createElement('select');
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.#i18n('selectOption');
    placeholder.disabled = true;
    placeholder.selected = true;
    select.append(placeholder);

    for (const [name] of provinces) {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = name;
      if (name.toLowerCase() === currentValue.toLowerCase()) {
        option.selected = true;
        placeholder.selected = false;
      }
      select.append(option);
    }

    return select;
  }

  /**
   * @param {string} currentValue
   * @returns {HTMLInputElement}
   */
  #buildRegionInput(currentValue) {
    const input = document.createElement('input');
    input.type = 'text';
    input.value = currentValue;
    return input;
  }

  /**
   * Persists whichever field the customer just edited, and — if that field
   * is currently showing a validation error — revalidates just that field
   * so the error clears or updates naturally as the customer types.
   * Delegated at the component level — rather than a per-field
   * `on:input`/`on:change` attribute in Liquid — so this keeps working
   * automatically across the State/Province field's select↔input swap, and
   * for any field added to `whatsapp-checkout-modal.liquid` in the future,
   * with no markup changes required here.
   *
   * @param {Event} event
   */
  #handleFieldChange = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.dataset.field) return;

    const element = /** @type {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} */ (event.target);
    if (element.getAttribute('aria-invalid') === 'true') {
      this.#validateField(element);
    }

    this.#saveCustomer();
  };

  /**
   * Saves the current value of every field in the registry to localStorage,
   * keyed by each field's `data-field`. The registry determines what gets
   * saved — this never names a specific field.
   */
  #saveCustomer() {
    /** @type {Record<string, string>} */
    const data = {};
    for (const field of this.fields) {
      if (!field.key) continue;
      data[field.key] = field.element.value;
    }

    try {
      localStorage.setItem(WhatsappCheckoutModal.#STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('[whatsapp-checkout] Could not save customer data:', error);
    }
  }

  /**
   * Reads and parses the persisted customer data. Never throws — returns
   * an empty object if localStorage is unavailable, blocked, or the stored
   * value is missing or invalid JSON.
   *
   * @returns {Record<string, string>}
   */
  #readPersistedCustomer() {
    try {
      const raw = localStorage.getItem(WhatsappCheckoutModal.#STORAGE_KEY);
      if (!raw) return {};

      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      console.warn('[whatsapp-checkout] Could not read persisted customer data:', error);
      return {};
    }
  }

  /**
   * Restores persisted customer data into the form. Called once each time
   * the modal opens, before it's shown.
   *
   * Country is restored first and in isolation, because `handleCountryChange`
   * may replace the State/Province element entirely — every other field is
   * restored only afterward. The region field is read via a direct
   * `querySelector` rather than `this.fields` at that specific point: this
   * class's ref list is refreshed by `Component`'s own `MutationObserver`,
   * which runs asynchronously, so it is not guaranteed to reflect a
   * same-tick replacement yet. Querying `[data-field-role="region"]`
   * reads the live DOM directly instead, sidestepping that timing question
   * without introducing a second observer.
   */
  #restoreCustomerData() {
    const persisted = this.#readPersistedCustomer();

    const countryField = this.#findFieldByRole('country');
    if (countryField) {
      this.#restoreCountry(countryField, persisted[countryField.key]);
    }

    this.handleCountryChange();

    const regionElement = this.querySelector('[data-field-role="region"]');
    if (
      (regionElement instanceof HTMLInputElement ||
        regionElement instanceof HTMLSelectElement ||
        regionElement instanceof HTMLTextAreaElement) &&
      regionElement.dataset.field
    ) {
      this.#restoreFieldValue(regionElement, persisted[regionElement.dataset.field]);
    }

    for (const field of this.fields) {
      if (field.role === 'country' || field.role === 'region') continue;
      this.#restoreFieldValue(field.element, persisted[field.key]);
    }
  }

  /**
   * Determines and applies the initial Country selection, in priority order:
   * 1. The persisted country, if it still matches an existing option.
   * 2. Otherwise Shopify's active localization country (`data-default-country`,
   *    rendered from `localization.country.name`), if it matches an existing
   *    option.
   * 3. Otherwise the Country field is left exactly as Liquid rendered it.
   *
   * @param {WhatsappFieldDescriptor} countryField
   * @param {unknown} persistedValue
   */
  #restoreCountry(countryField, persistedValue) {
    const select = /** @type {HTMLSelectElement} */ (countryField.element);

    if (typeof persistedValue === 'string' && persistedValue !== '') {
      const hasMatchingOption = [...select.options].some((option) => option.value === persistedValue);
      if (hasMatchingOption) {
        select.value = persistedValue;
        return;
      }
    }

    const defaultCountry = select.dataset.defaultCountry;
    if (!defaultCountry) return;

    const normalizedDefault = defaultCountry.trim().toLowerCase();
    const match = [...select.options].find(
      (option) =>
        option.value.trim().toLowerCase() === normalizedDefault || option.text.trim().toLowerCase() === normalizedDefault
    );
    if (match) select.value = match.value;
  }

  /**
   * Restores a persisted value into a field, respecting its current control
   * type. Selects are only updated when a matching option exists; inputs
   * and textareas accept any stored string.
   *
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
   * @param {unknown} storedValue
   */
  #restoreFieldValue(element, storedValue) {
    if (typeof storedValue !== 'string' || storedValue === '') return;

    if (element instanceof HTMLSelectElement) {
      const hasMatchingOption = [...element.options].some((option) => option.value === storedValue);
      if (hasMatchingOption) element.value = storedValue;
      return;
    }

    element.value = storedValue;
  }

  /**
   * Runs full-form validation and, once valid, shows the Order Review
   * screen built from the current cart payload and field registry.
   */
  handleContinue = () => {
    const isValid = this.#validateFields();
    if (!isValid) return;

    this.#renderReview();
    this.#showView('review');
    this.refs.reviewHeading.focus();
  };

  /**
   * Returns to the customer-details form from the Order Review screen,
   * without resetting or losing any entered values — fields are never
   * destroyed, only hidden.
   */
  handleEdit = () => {
    this.#showView('form');
    this.refs.continueButton.focus();
  };

  /**
   * Confirms the order: builds the WhatsApp message, opens the wa.me link
   * in a new tab, and shows a brief success state before closing the
   * modal. Guards against duplicate submissions.
   */
  handleConfirm = () => {
    if (this.#isSubmitting) return;
    this.#isSubmitting = true;

    const message = this.#buildMessage();
    const number = this.#resolveWhatsAppNumber();
    const url = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;

    window.open(url, '_blank', 'noopener');

    this.#showView('success');

    window.setTimeout(() => {
      this.closeDialog();
    }, 1800);
  };

  /**
   * Validates every field in the registry using the native Constraint
   * Validation API, updating each field's error UI accordingly, and moves
   * focus to the first invalid field if any.
   *
   * @returns {boolean} Whether every field is currently valid.
   */
  #validateFields() {
    let isValid = true;
    /** @type {WhatsappFieldDescriptor | undefined} */
    let firstInvalidField;

    for (const field of this.fields) {
      const fieldIsValid = this.#validateField(field.element);
      if (!fieldIsValid) {
        isValid = false;
        firstInvalidField ??= field;
      }
    }

    firstInvalidField?.element.focus();

    return isValid;
  }

  /**
   * Validates a single field against its own native constraints and
   * updates its error UI.
   *
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
   * @returns {boolean}
   */
  #validateField(element) {
    const errorContainer = this.#getErrorContainer(element);

    if (element.checkValidity()) {
      this.#clearFieldError(element, errorContainer);
      return true;
    }

    this.#showFieldError(element, errorContainer, this.#getValidationMessage(element));
    return false;
  }

  /**
   * Finds a field's error container via its own `aria-describedby` — the
   * relationship already established in `whatsapp-checkout-modal.liquid` —
   * rather than a hardcoded field-name-to-error-id map.
   *
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
   * @returns {HTMLElement | null}
   */
  #getErrorContainer(element) {
    const errorId = element.getAttribute('aria-describedby');
    if (!errorId) return null;
    return this.querySelector(`#${errorId}`);
  }

  /**
   * Determines the translated message for a field's current validation
   * failure. A field may override either message via `data-error-required`
   * / `data-error-invalid` in Liquid; otherwise a generic, translated
   * fallback is used.
   *
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
   * @returns {string}
   */
  #getValidationMessage(element) {
    if (element.validity.valueMissing) {
      return element.dataset.errorRequired || this.#i18n('fieldRequired');
    }
    return element.dataset.errorInvalid || this.#i18n('fieldInvalid');
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
   * @param {HTMLElement | null} errorContainer
   * @param {string} message
   */
  #showFieldError(element, errorContainer, message) {
    element.setAttribute('aria-invalid', 'true');
    element.classList.add('whatsapp-checkout-field__input--invalid');

    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.hidden = false;
    }
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
   * @param {HTMLElement | null} errorContainer
   */
  #clearFieldError(element, errorContainer) {
    element.removeAttribute('aria-invalid');
    element.classList.remove('whatsapp-checkout-field__input--invalid');

    if (errorContainer) {
      errorContainer.textContent = '';
      errorContainer.hidden = true;
    }
  }

  /**
   * Clears any validation error state left over from a previous opening.
   */
  #clearAllFieldErrors() {
    for (const field of this.fields) {
      this.#clearFieldError(field.element, this.#getErrorContainer(field.element));
    }
  }

  /**
   * Switches between the customer-details form, the Order Review screen,
   * and the post-send success state. Uses the same `ref` + `hidden` toggle
   * already established in this theme for switching between view states
   * (see `GiftCardRecipientForm`'s self/recipient toggle) — not a new
   * mechanism.
   *
   * @param {'form' | 'review' | 'success'} view
   */
  #showView(view) {
    this.refs.formView.hidden = view !== 'form';
    this.refs.reviewView.hidden = view !== 'review';
    this.refs.successView.hidden = view !== 'success';
  }

  /**
   * Reads a field's own visible label text, so the Review screen never
   * duplicates a field's label in a separate attribute — whatever
   * `<label>` Liquid already renders is authoritative.
   *
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
   * @returns {string}
   */
  #getFieldLabel(element) {
    const label = this.querySelector(`label[for="${element.id}"]`);
    return label?.textContent?.trim() ?? '';
  }

  /**
   * Builds the Order Review screen's content from the current cart payload
   * and field registry. Read-only: never writes to localStorage or to any
   * field's value.
   */
  #renderReview() {
    this.#renderReviewProducts();
    this.#renderReviewTotals();
    this.#renderReviewCustomer();
  }

  /**
   * Renders the Products list from `this.#cartPayload`, built with safe
   * DOM APIs (`textContent`) rather than string-concatenated HTML.
   */
  #renderReviewProducts() {
    const container = this.refs.reviewProducts;
    container.replaceChildren();

    const items = this.#cartPayload?.items ?? [];
    const list = document.createElement('ul');
    list.className = 'whatsapp-checkout-review__product-list';

    for (const item of items) {
      const listItem = document.createElement('li');
      listItem.className = 'whatsapp-checkout-review__product';

      const media = document.createElement('div');
      media.className = 'whatsapp-checkout-review__product-media';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        media.append(image);
      } else {
        media.classList.add('whatsapp-checkout-review__product-media--empty');
      }
      listItem.append(media);

      const info = document.createElement('div');
      info.className = 'whatsapp-checkout-review__product-info';

      const title = document.createElement('span');
      title.className = 'whatsapp-checkout-review__product-title';
      title.textContent = item.title;
      info.append(title);

      if (item.variantTitle) {
        const variant = document.createElement('span');
        variant.className = 'whatsapp-checkout-review__product-variant';
        variant.textContent = item.variantTitle;
        info.append(variant);
      }

      const row = document.createElement('div');
      row.className = 'whatsapp-checkout-review__product-row';

      const meta = document.createElement('span');
      meta.className = 'whatsapp-checkout-review__product-meta';
      meta.textContent = `${item.quantity} × ${item.price}`;
      row.append(meta);

      const total = document.createElement('span');
      total.className = 'whatsapp-checkout-review__product-total';
      total.textContent = item.lineTotal;
      row.append(total);

      info.append(row);
      listItem.append(info);
      list.append(listItem);
    }

    container.append(list);
  }

  /**
   * Renders the Subtotal and item-count rows from `this.#cartPayload`.
   */
  #renderReviewTotals() {
    const container = this.refs.reviewTotals;
    container.replaceChildren();

    const payload = this.#cartPayload;
    if (!payload) return;

    const subtotalRow = document.createElement('div');
    subtotalRow.className = 'whatsapp-checkout-review__totals-row';
    const subtotalLabel = document.createElement('span');
    subtotalLabel.textContent = this.#i18n('subtotalLabel');
    const subtotalValue = document.createElement('span');
    subtotalValue.className = 'whatsapp-checkout-review__totals-value';
    subtotalValue.textContent = payload.subtotal;
    subtotalRow.append(subtotalLabel, subtotalValue);

    const countRow = document.createElement('div');
    countRow.className = 'whatsapp-checkout-review__totals-row';
    const countLabel = document.createElement('span');
    countLabel.textContent = this.#i18n('itemCountLabel');
    const countValue = document.createElement('span');
    countValue.className = 'whatsapp-checkout-review__totals-value';
    countValue.textContent = String(payload.itemCount);
    countRow.append(countLabel, countValue);

    container.append(subtotalRow, countRow);
  }

  /**
   * Renders the Customer Information list and, separately, the Notes
   * section — built entirely from `this.fields`, with no field names
   * hardcoded. A field appears in the Customer Information list only when
   * `data-review="true"` and it isn't the field with
   * `data-field-role="notes"`, which is rendered in its own section
   * instead. Blank optional fields are omitted from both.
   */
  #renderReviewCustomer() {
    const customerContainer = this.refs.reviewCustomer;
    const notesContainer = this.refs.reviewNotes;
    customerContainer.replaceChildren();
    notesContainer.replaceChildren();
    notesContainer.hidden = true;

    const list = document.createElement('dl');
    list.className = 'whatsapp-checkout-review__customer-list';

    for (const field of this.fields) {
      if (!field.includeInReview || field.role === 'notes') continue;

      const value = this.#getFieldDisplayValue(field.element);
      if (!value) continue;

      const item = document.createElement('div');
      item.className = 'whatsapp-checkout-review__customer-item';
      if (field.role === 'name') {
        item.classList.add('whatsapp-checkout-review__customer-item--wide');
      }

      const dt = document.createElement('dt');
      dt.textContent = this.#getFieldLabel(field.element);
      const dd = document.createElement('dd');
      dd.textContent = value;

      item.append(dt, dd);
      list.append(item);
    }

    customerContainer.append(list);

    const notesField = this.#findFieldByRole('notes');
    if (!notesField) return;

    const notesValue = this.#getFieldDisplayValue(notesField.element);
    if (!notesValue) return;

    const heading = document.createElement('h4');
    heading.textContent = this.#getFieldLabel(notesField.element);
    const text = document.createElement('p');
    text.textContent = notesValue;
    notesContainer.append(heading, text);
    notesContainer.hidden = false;
  }

  /**
   * Resolves which WhatsApp number to send the order to.
   *
   * v1: always the single number configured in the Theme Editor
   * (`data-phone`, set from `settings.whatsapp_number`). Kept as its own
   * method with a single call site so future per-country/region routing
   * can replace this body without touching anything else that calls it.
   *
   * @returns {string}
   */
  #resolveWhatsAppNumber() {
    return this.dataset.phone ?? '';
  }

  /**
   * Prefixes a section title with its emoji, unless
   * `settings.whatsapp_message_use_emojis` (`data-use-emojis`) is off.
   *
   * @param {string} icon
   * @param {string} text
   * @returns {string}
   */
  #sectionTitle(icon, text) {
    const label = this.dataset.useEmojis === 'true' ? `${icon} ${text}` : text;
    return `*${label}*`;
  }

  /**
   * Assembles the WhatsApp order message from independent sections. Each
   * section returns its own lines, or `null`/an empty array if it has
   * nothing to contribute — that section (including its heading) is then
   * left out of the message entirely, with no per-section special-casing
   * here.
   *
   * @returns {string}
   */
  #buildMessage() {
    const separator = '--------------------------------------------------';
    const sections = [
      this.#buildHeaderSection(),
      this.#buildProductsSection(),
      this.#buildTotalsSection(),
      this.#buildCustomerSection(),
      this.#buildNotesSection(),
      this.#buildCartLinkSection(),
      this.#buildFooterSection(),
    ];

    return sections
      .filter((lines) => lines && lines.length > 0)
      .map((lines) => /** @type {string[]} */ (lines).join('\n'))
      .join(`\n${separator}\n`);
  }

  /**
   * @returns {string[]}
   */
  #buildHeaderSection() {
    return [this.#sectionTitle('🛒', this.#i18n('messageHeader'))];
  }

  /**
   * @returns {string[] | null}
   */
  #buildProductsSection() {
    const items = this.#cartPayload?.items ?? [];
    if (items.length === 0) return null;

    const lines = [this.#sectionTitle('📦', this.#i18n('messageProductsHeader')), ''];
    items.forEach((item, index) => {
      if (index > 0) lines.push('');
      lines.push(`• *${item.title}*`);
      if (item.variantTitle) lines.push(`   ${item.variantTitle}`);
      lines.push(`   ${item.quantity} × ${item.price} = ${item.lineTotal}`);
    });
    return lines;
  }

  /**
   * @returns {string[] | null}
   */
  #buildTotalsSection() {
    const payload = this.#cartPayload;
    if (!payload) return null;

    return [
      this.#sectionTitle('💰', this.#i18n('messageTotalsHeader')),
      `${this.#i18n('subtotalLabel')}: ${payload.subtotal}`,
      `${this.#i18n('itemCountLabel')}: ${payload.itemCount}`,
    ];
  }

  /**
   * @returns {string[] | null}
   */
  #buildCustomerSection() {
    const lines = [this.#sectionTitle('👤', this.#i18n('messageCustomerHeader'))];
    let hasContent = false;

    for (const field of this.fields) {
      if (!field.includeInMessage || field.role === 'notes') continue;

      const value = this.#getFieldDisplayValue(field.element);
      if (!value) continue;

      lines.push(`${this.#getFieldLabel(field.element)}: ${value}`);
      hasContent = true;
    }

    return hasContent ? lines : null;
  }

  /**
   * @returns {string[] | null}
   */
  #buildNotesSection() {
    const notesField = this.#findFieldByRole('notes');
    if (!notesField || !notesField.includeInMessage) return null;

    const value = this.#getFieldDisplayValue(notesField.element);
    if (!value) return null;

    return [this.#sectionTitle('📝', this.#i18n('messageNotesHeader')), value];
  }

  /**
   * @returns {string[] | null}
   */
  #buildCartLinkSection() {
    const url = this.#cartPayload?.cartUrl;
    if (!url) return null;

    return [this.#sectionTitle('🔗', this.#i18n('messageCartLinkHeader')), url];
  }

  /**
   * @returns {string[] | null}
   */
  #buildFooterSection() {
    const signature = this.dataset.footerSignature;
    return signature ? [signature] : null;
  }
}

if (!customElements.get('whatsapp-checkout-modal')) {
  customElements.define('whatsapp-checkout-modal', WhatsappCheckoutModal);
}

/**
 * A "Buy via WhatsApp" button rendered alongside a product's Add to Cart
 * button (see `blocks/whatsapp-buy-button.liquid`). Reads the currently
 * selected variant and quantity live, at click time, from the same
 * product form Shopify's own Add to Cart button submits, builds a payload
 * with the exact same shape the cart already produces, and hands off to
 * the same `WhatsappCheckoutModal` via its public `openFromProduct`
 * method — no separate checkout system, no second customer form.
 *
 * @typedef {object} WhatsappBuyButtonRefs
 * @property {HTMLButtonElement} button
 * @extends {Component<WhatsappBuyButtonRefs>}
 */
export class WhatsappBuyButtonComponent extends Component {
  requiredRefs = ['button'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Reads the current variant and quantity from the product form and
   * opens the WhatsApp Checkout modal with exactly that selection.
   */
  handleClick = () => {
    const form = document.getElementById(this.dataset.formId ?? '');
    if (!(form instanceof HTMLFormElement)) return;

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || 1;

    const payload = this.#readProductPayload();
    const variant = payload?.variants.find((candidate) => String(candidate.id) === String(variantId));
    if (!payload || !variant) return;

    const lineTotal = formatMoney(variant.priceCents * quantity, payload.moneyFormat, payload.currencyCode);

    const modal = /** @type {any} */ (document.getElementById('whatsapp-checkout-modal'));
    if (!modal || typeof modal.openFromProduct !== 'function') return;

    modal.openFromProduct({
      items: [
        {
          title: this.dataset.productTitle ?? '',
          variantTitle: variant.title,
          quantity,
          price: variant.price,
          lineTotal,
          image: variant.image,
        },
      ],
      subtotal: lineTotal,
      itemCount: quantity,
      cartUrl: payload.cartUrl,
      shopName: payload.shopName,
    });
  };

  /**
   * Reads and parses this button's product/variant JSON payload.
   *
   * @returns {WhatsappProductPayload | null}
   */
  #readProductPayload() {
    const node = document.getElementById(this.dataset.payloadId ?? '');
    if (!(node instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(node.textContent ?? '');
    } catch (error) {
      console.warn('[whatsapp-checkout] Could not parse product payload:', error);
      return null;
    }
  }

  /**
   * Keeps the button's enabled state in sync when the customer selects a
   * different variant. Listens to the same public `ProductSelectEvent`
   * `ProductFormComponent` already listens to — nothing private of its is
   * touched.
   *
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      ?.then((/** @type {{ detail?: { resource?: { available?: boolean } | null } }} */ result) => {
        const { resource } = result?.detail ?? {};
        this.refs.button.disabled = resource == null || resource.available === false;
      })
      .catch(() => {});
  };
}

if (!customElements.get('whatsapp-buy-button-component')) {
  customElements.define('whatsapp-buy-button-component', WhatsappBuyButtonComponent);
}

/**
 * @typedef {object} WhatsappCartItem
 * @property {string} title - The product's title.
 * @property {string} variantTitle - The variant's title, or an empty string for products with only a default variant.
 * @property {number} quantity
 * @property {string} price - Pre-formatted via Shopify's `money` filter.
 * @property {string} lineTotal - Pre-formatted via Shopify's `money` filter.
 * @property {string | null} image - The item's product image URL, or `null` if it has none.
 */

/**
 * @typedef {object} WhatsappCartPayload
 * @property {WhatsappCartItem[]} items
 * @property {string} subtotal - Pre-formatted via Shopify's `money` filter.
 * @property {number} itemCount
 * @property {string} cartUrl - Absolute, Market-correct cart URL.
 * @property {string} shopName
 */

/**
 * @typedef {object} WhatsappProductVariant
 * @property {string | number} id
 * @property {string} title - Empty string for products with only a default variant.
 * @property {string} price - Pre-formatted via Shopify's `money` filter.
 * @property {number} priceCents - Raw price in the shop's smallest currency unit.
 * @property {string | null} image - The variant's (or product's) image URL, or `null` if it has none.
 */

/**
 * @typedef {object} WhatsappProductPayload
 * @property {WhatsappProductVariant[]} variants
 * @property {string} shopName
 * @property {string} cartUrl
 * @property {string} moneyFormat - `shop.money_format`, used with `formatMoney` to compute a line total for the selected quantity.
 * @property {string} currencyCode
 */

/**
 * @typedef {object} WhatsappFieldDescriptor
 * @property {string} key - The field's unique identifier, from `data-field`.
 * @property {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} element
 * @property {boolean} required
 * @property {boolean} includeInReview - Whether to show this field on the Order Review screen.
 * @property {boolean} includeInMessage - Whether to include this field in the WhatsApp message.
 * @property {string | undefined} role - Optional behavioral role, from `data-field-role` (e.g. "country", "region").
 */
